import {
  StethoscopeIcon,
  ClockIcon,
  SplitIcon,
  ScanIcon,
  TriangleAlertIcon,
  LayersIcon,
  GitBranchIcon,
  ClipboardCheckIcon,
  Grid3x3Icon,
  FileTextIcon,
  FlagIcon,
} from "lucide-react";

export const CHECKOUT_URL = "https://implant-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/76kiJAGgtPDMpWP9SCACey/IMPLANT-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/9DqjUxM83TCSBGJyWjxzqt/MICHAEL_CARTER_-_IMPLANTES.png",
};

export const planningQuestions = [
  "Is this patient actually a suitable candidate?",
  "What risk factors could affect the prognosis?",
  "What does the CBCT really tell me?",
  "Which anatomical structures could change my plan?",
  "Where should the implant ultimately be positioned?",
  "What needs to be decided before surgery?",
];

export const beforeItems = [
  "I spent too much time trying to figure out where to start.",
  "I jumped between notes, courses, and different sources before making a decision.",
  "I wasn't always sure whether I should take a case, prepare it, or refer it.",
  "I worried about overlooking an important anatomical factor.",
  "Every patient seemed to require a completely different approach.",
  "I reached the planning stage with too many unanswered questions.",
];

export const afterItems = [
  "I follow a defined sequence for analyzing every case.",
  "I identify the factors that could change the treatment decision first.",
  "I follow a structured pathway for reviewing the CBCT.",
  "I systematically identify the relevant anatomical structures.",
  "I organize implant position, angulation, and safety considerations before surgery.",
  "I enter the surgical phase with the key decisions already structured.",
];

export const painPoints = [
  { icon: StethoscopeIcon, text: "You have an implant patient in your chair and you're not completely sure what you should evaluate first." },
  { icon: ClockIcon, text: "You spend too much time going back through notes, videos, and different resources before planning a case." },
  { icon: SplitIcon, text: "You sometimes hesitate when deciding whether you should take a case, prepare it, or refer it." },
  { icon: ScanIcon, text: "You review CBCT scans, but you want a structured process that helps you avoid overlooking important factors." },
  { icon: TriangleAlertIcon, text: "You worry about discovering something during surgery that should have been identified during planning." },
  { icon: LayersIcon, text: "Before a complex implant case, you feel like there are simply too many variables to consider at once." },
];

export const volumes = [
  {
    number: "I",
    color: "#1F4FA3",
    title: "Should I take this case?",
    subtitle: "Case Selection & Initial Assessment",
    description: "Learn how to systematically evaluate a patient before you start thinking about implant placement.",
    inside: ["Initial patient assessment", "Risk factors", "Prognostic factors", "Conditions that may change the treatment plan", "Case preparation criteria", "Referral considerations", "Case-selection checklist", "Initial decision matrix"],
  },
  {
    number: "II",
    color: "#1D6B4A",
    title: "Know what to look for on CBCT",
    subtitle: "The Structured CBCT Review Method",
    description: "A step-by-step framework for organizing your CBCT review and identifying the information you need before planning.",
    inside: ["Systematic CBCT review pathway", "Relevant anatomy", "Available bone height", "Ridge width", "Critical anatomical structures", "Key measurements", "Radiographic risk factors", "CBCT review checklist"],
  },
  {
    number: "III",
    color: "#8A5A1F",
    title: "The 7 things I need to define before planning",
    subtitle: "Turn Clinical Information Into Clear Decisions",
    description: "A structured framework for turning diagnostic information into an organized implant plan.",
    inside: ["Case selection", "Treatment site", "Available bone", "Anatomy", "Implant position", "Angulation", "Safety considerations", "Prosthetic considerations", "Planning matrix"],
  },
  {
    number: "IV",
    color: "#A3232E",
    title: "Plan the implant before you enter surgery",
    subtitle: "Your Pre-Surgical Planning Blueprint",
    description: "A structured method for organizing the critical surgical decisions before the patient enters the surgical chair.",
    inside: ["Planning sequence", "3D implant positioning", "Angulation", "Depth considerations", "Safety margins", "Anatomical considerations", "Surgical preparation", "Pre-operative checklist", "Master Case Planning Sheet"],
  },
  {
    number: "V",
    color: "#5B2A8C",
    title: "Turn the plan into a surgical sequence",
    subtitle: "From Pre-Op Decisions to Surgical Execution",
    description: "Learn how to translate the decisions made during planning into an organized surgical workflow.",
    inside: ["Surgical preparation", "Surgical sequence", "Drilling sequence", "Primary stability considerations", "Soft-tissue management", "Flap considerations", "Suturing", "Variable control", "Surgical checklist"],
  },
  {
    number: "VI",
    color: "#1E2F5C",
    title: "Review the case before repeating the same mistake",
    subtitle: "The Clinical Case Review System",
    description: "Because improving isn't only about performing more implant cases. It's also about understanding why each decision was made and what can be improved next time.",
    inside: ["Case review framework", "Decision tracking", "Post-operative review", "Common planning errors", "Improvement points", "Case review checklist", "Case documentation system", "Personal scorecard"],
  },
];

export const tools = [
  { icon: GitBranchIcon, title: "Clinical Decision Trees", text: "Follow a logical sequence instead of trying to analyze every case from scratch." },
  { icon: ClipboardCheckIcon, title: "Professional Checklists", text: "Review the critical points before moving forward." },
  { icon: Grid3x3Icon, title: "Risk & Planning Matrices", text: "Organize patient factors, anatomy, risk, and planning considerations." },
  { icon: FileTextIcon, title: "Case Planning Sheets", text: "Document every case using the same structured process." },
  { icon: FlagIcon, title: "Red Flag System", text: "Identify warning signs that should make you stop, reassess, or reconsider the plan." },
];

export const bonuses = [
  {
    number: 1,
    title: "30 Cases Before Surgery™",
    value: 47,
    image: "https://cdn.magicpatterns.com/uploads/7zUhcEAXRYqCH8vx1g2rSx/BONUS_1_IMPLANT-EN.png",
    text: "30 clinical cases designed to train your decision-making before facing the next real case. Analyze each case, make your decision, and then compare your reasoning against the structured framework.",
    tags: [] as string[],
  },
  {
    number: 2,
    title: "CBCT Red Flags™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/7rGyFd6UHY9Weax78vSdxw/BONUS_2_IMPLANT-EN.png",
    text: "A rapid-reference guide to the warning signs you should review before moving forward with implant planning. A visual tool designed to keep the most important considerations in front of you during case evaluation.",
    tags: [] as string[],
  },
  {
    number: 3,
    title: "The Implant Case Scorecard™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/6zzZYfgiXzZpKJt4jmT4jk/BONUS_3_IMPLANT-EN.png",
    text: "A structured scoring matrix for organizing the different dimensions of an implant case. It helps you identify the areas that deserve the most attention before moving forward.",
    tags: ["Patient factors", "Risk", "Anatomy", "Bone", "CBCT findings", "Planning", "Complexity"],
  },
  {
    number: 4,
    title: "Pre-Surgery Master Checklist™",
    value: 19,
    image: "https://cdn.magicpatterns.com/uploads/w7hhU9vRv79AUyYUHWNAnk/BONUS_4_IMPLANT-EN.png",
    text: "The final pre-operative review checklist. One simple sheet to verify that the key decisions have been addressed before surgery begins.",
    tags: [] as string[],
  },
  {
    number: 5,
    title: "Implant Decision Cards™",
    value: 17,
    image: "https://cdn.magicpatterns.com/uploads/53xsecsMs9Qs3kvA1iRQ8L/BONUS_5_IMPLANT-EN.png",
    text: "Quick-reference clinical decision cards — a collection of visual cards you can keep at hand.",
    tags: ["Case selection", "CBCT", "Anatomy", "Implant position", "Angulation", "Safety considerations", "Planning", "Surgery", "Stability"],
  },
];

export const guaranteeSteps = [
  "Explore all 6 volumes.",
  "Review the checklists.",
  "Use the decision trees.",
  "Work through the cases.",
  "Test the system against your own planning workflow.",
];

export const testimonials = [
  {
    name: "Dr. Laura Mendoza",
    role: "General Dentist · Houston, TX",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/9f10256b-f995-4d89-a38d-f3f5a7b1ec82.jpg",
    quote: "I used to reopen three different courses before every implant consult. Now I run the same sequence every time and I walk into planning knowing exactly what I still need to confirm.",
  },
  {
    name: "Dr. James Whitfield",
    role: "Restorative Dentist · Manchester, UK",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/2af6fdb7-9a5e-4dac-84f1-519ce08b673f.jpg",
    quote: "The CBCT review pathway alone was worth it. I stopped jumping around the scan and started reading it in a fixed order. My referral decisions feel far more deliberate.",
  },
  {
    name: "Dr. Priya Nair",
    role: "Associate Dentist · Toronto, Canada",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/19e7bf03-b247-4415-bccd-02743bc8c827.jpg",
    quote: "As someone early in implant work, the red flag system and the scorecard give me a clear picture of when to prepare a case and when to refer it. Printed and on my desk.",
  },
  {
    name: "Dr. Marcus Bennett",
    role: "Implant Dentist · Atlanta, GA",
    image: "https://cdn.magicpatterns.com/patterns/generated-images/faad1df4-c1be-4914-916a-7c3596acfd05.jpg",
    quote: "I share the Master Case Planning Sheet with my assistant before every surgery. Everyone is on the same page and there are fewer surprises once we're in the chair.",
  },
];

export const faqs = [
  { q: "How will I receive the material?", a: "Immediately after payment, you'll receive access to download the 6 volumes and 5 bonuses in digital format." },
  { q: "Do I need to be a specialist?", a: "The system is designed as an educational decision-support resource for dental professionals interested in structuring their implant case assessment and planning process. Your scope of practice and level of clinical experience will determine which cases are appropriate for you." },
  { q: "Do I need previous implant experience?", a: "The system walks through case selection, diagnosis, CBCT review, planning, surgical preparation, and case review. However, it does not replace formal clinical education, hands-on training, supervision, or professional judgment." },
  { q: "Are the files downloadable?", a: "Yes. The materials are designed for digital consultation and include printable tools and checklists." },
  { q: "Is this a one-time payment?", a: "Yes. You pay $27 once. There are no monthly payments." },
  { q: "Do I get lifetime access?", a: "Yes, while the product and its delivery platform remain available." },
  { q: "Is this a replacement for an implantology course?", a: "No. IMPLANT DECISION SYSTEM™ is not intended to replace formal implantology education, clinical training, supervision, or individual professional judgment. It is designed to help structure the case-analysis and planning process." },
  { q: "What if I don't like it?", a: "You have a 7-day guarantee. If the product doesn't meet your expectations, you can request a refund according to the terms of the guarantee." },
];

export const recentBuyers = [
  { name: "Carolina R.", country: "Mexico" },
  { name: "Andrés M.", country: "Colombia" },
  { name: "Lucía F.", country: "Spain" },
  { name: "Javier P.", country: "Argentina" },
  { name: "Valentina S.", country: "Chile" },
  { name: "Diego H.", country: "Peru" },
  { name: "Camila T.", country: "Ecuador" },
  { name: "Sebastián G.", country: "Uruguay" },
  { name: "Mariana L.", country: "Costa Rica" },
  { name: "Alejandro V.", country: "Dominican Republic" },
  { name: "Isabella C.", country: "Panama" },
  { name: "Rafael O.", country: "Puerto Rico" },
];
